// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview One-time migration that promotes `lastHeartbeat` and
 * `containerStatus` from the TeamMember `metadata` blob to schema-level
 * fields.
 *
 * Idempotent — safe to run multiple times. Records where `lastHeartbeat`
 * is already set at the schema level are skipped. Per-record errors are
 * accumulated in the returned {@link PromotionResult.errors} array rather
 * than aborting the whole run.
 */

import type { TeamMemberModel } from '../models/TeamMember'

export interface PromotionDependencies {
  orgId: string
  getTeamMembers(): Promise<TeamMemberModel[]>
  updateTeamMember(id: string, patch: Partial<TeamMemberModel>): Promise<void>
}

export interface PromotionResult {
  migrated: number
  skipped: number
  errors: Array<{ id: string; reason: string }>
}

/**
 * Promote `lastHeartbeat` and `containerStatus` from metadata to
 * schema-level fields on every TeamMember record in the given org.
 *
 * Idempotent — records where `lastHeartbeat` is already set at the
 * schema level are skipped. Records with no metadata values to promote
 * are also skipped.
 *
 * All side effects are injected via `deps` so this function is fully
 * testable without a live database.
 */
export async function promoteTeamMemberAgentFields(
  deps: PromotionDependencies,
): Promise<PromotionResult> {
  let migrated = 0
  let skipped = 0
  const errors: PromotionResult['errors'] = []

  const members = await deps.getTeamMembers()

  for (const member of members) {
    try {
      // Already migrated — skip
      if (member.lastHeartbeat) {
        skipped++
        continue
      }

      const metadata = member.metadata ?? {}
      const metaHeartbeat = metadata.lastHeartbeat as string | undefined
      const metaContainerStatus = metadata.containerStatus as
        | TeamMemberModel['containerStatus']
        | undefined

      // Nothing to promote — skip
      if (!metaHeartbeat && !metaContainerStatus) {
        skipped++
        continue
      }

      const patch: Partial<TeamMemberModel> = {}
      if (metaHeartbeat) {
        patch.lastHeartbeat = metaHeartbeat
      }
      patch.containerStatus = metaContainerStatus ?? 'unknown'

      await deps.updateTeamMember(member.id, patch)
      migrated++
    } catch (err) {
      errors.push({ id: member.id, reason: String(err) })
    }
  }

  return { migrated, skipped, errors }
}
